import React from "react";
import { Form, Input, TextArea, Table, Checkbox } from "semantic-ui-react";
import "./Paso2Screening.scss";

const fmt = (v, d = 1) => (isFinite(v) ? Number(v).toLocaleString("es-AR", { minimumFractionDigits: d, maximumFractionDigits: d }) : "—");

// Portado de vP2() del prototipo (línea 581). Las 15 categorías NO se
// hardcodean acá: se leen de computed.calc.scope3Detail, que ya viene
// con {id, n, title, note, value} desde sbtiEngine.calc() — un solo
// lugar de verdad para la lista de categorías (server/services/sbtiEngine.js).
export function Paso2Screening({ record, computed, patchSection }) {
  const inv = record.inventory || {};
  const cob = record.coverageSelection || {};
  const params = computed?.params || {};
  const calc = computed?.calc || { scope3Detail: [] };

  const setInv = (patch) => patchSection("inventory", { ...inv, ...patch });
  const setCoverage = (patch) => patchSection("coverageSelection", { ...cob, ...patch }, { immediate: true });
  const setScope3 = (catId, value) => setInv({ scope3: { ...inv.scope3, [catId]: value } });

  const cobertura_a3 = params.cobertura_a3 ?? 67;
  const umbral_a3 = params.umbral_a3 ?? 40;
  const flag_umbral = params.flag_umbral ?? 20;

  return (
    <div className="sbti-paso2">
      <div className="sbti-pagehead">
        <div className="sbti-crumb">Paso 2 · Screening</div>
        <h2>Screening Alcance 3 <span className="sbti-pgram"><span>15 categorías</span></span> + FLAG</h2>
        <p className="sbti-sub">
          Screening completo obligatorio para determinar significancia (GHG Protocol Scope 3 Standard). Si el
          Alcance 3 es ≥ {umbral_a3}% del total A1+2+3, el objetivo de Alcance 3 es obligatorio y debe cubrir ≥{" "}
          {cobertura_a3}% del A3. La Categoría 11 (uso, indirecto) es excluible en indumentaria/calzado.
        </p>
      </div>

      <div className="sbti-kpis">
        <div className="sbti-kpi"><div className="sbti-kpi__l">A3 total</div><div className="sbti-kpi__v">{fmt(calc.scope3Total)} <span>tCO₂e</span></div></div>
        <div className="sbti-kpi"><div className="sbti-kpi__l">A3 elegible {inv.excludeCat11 ? "(sin Cat 11)" : ""}</div><div className="sbti-kpi__v">{fmt(calc.scope3Eligible)} <span>tCO₂e</span></div></div>
        <div className={`sbti-kpi ${calc.scope3Required ? "sbti-kpi--alert" : ""}`}><div className="sbti-kpi__l">Peso del A3</div><div className="sbti-kpi__v">{fmt(calc.scope3Percent)} <span>%</span></div></div>
        <div className="sbti-kpi"><div className="sbti-kpi__l">¿Objetivo A3 obligatorio?</div><div className="sbti-kpi__v" style={{ fontSize: 16 }}>{calc.scope3Total ? (calc.scope3Required ? `SÍ (≥${umbral_a3}%)` : `No (<${umbral_a3}%)`) : "—"}</div></div>
        <div className={`sbti-kpi ${calc.scope3Required && calc.coveragePercent < cobertura_a3 ? "sbti-kpi--alert" : ""}`}><div className="sbti-kpi__l">Cobertura elegida</div><div className="sbti-kpi__v">{fmt(calc.coveragePercent)} <span>% (mín. {cobertura_a3}%)</span></div></div>
      </div>

      <div className="sbti-card">
        <h3><span className="sbti-tag">DATOS</span> Emisiones por categoría y selección de cobertura del objetivo</h3>
        <Checkbox
          label={`Excluir Cat 11 (uso indirecto) del cálculo del ${umbral_a3}% y de la base de cobertura — permitido por la guía sectorial §6.2 para indumentaria/calzado.`}
          checked={!!inv.excludeCat11}
          onChange={(_, d) => setInv({ excludeCat11: d.checked })}
          className="sbti-paso2__excl"
        />
        <Table celled compact className="sbti-paso2__table">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>#</Table.HeaderCell>
              <Table.HeaderCell>Categoría</Table.HeaderCell>
              <Table.HeaderCell>tCO₂e</Table.HeaderCell>
              <Table.HeaderCell>% del A3</Table.HeaderCell>
              <Table.HeaderCell>Incluir en objetivo</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {calc.scope3Detail.map((cat) => {
              const excluded = inv.excludeCat11 && cat.id === "cat11";
              const pct = calc.scope3Total > 0 ? (cat.value / calc.scope3Total) * 100 : 0;
              return (
                <Table.Row key={cat.id} disabled={excluded} style={excluded ? { opacity: 0.55 } : undefined}>
                  <Table.Cell>{cat.n}</Table.Cell>
                  <Table.Cell>
                    {cat.title}
                    {cat.note && <div className="sbti-hint">{cat.note}</div>}
                  </Table.Cell>
                  <Table.Cell>
                    <Input type="number" step="0.01" value={inv.scope3?.[cat.id] || ""} onChange={(_, d) => setScope3(cat.id, d.value)} />
                  </Table.Cell>
                  <Table.Cell>{cat.value ? fmt(pct) : "—"}</Table.Cell>
                  <Table.Cell textAlign="center">
                    <Checkbox checked={!!cob[cat.id]} disabled={excluded} onChange={(_, d) => setCoverage({ [cat.id]: d.checked })} />
                  </Table.Cell>
                </Table.Row>
              );
            })}
            <Table.Row>
              <Table.Cell />
              <Table.Cell><b>Total A3</b></Table.Cell>
              <Table.Cell><b>{fmt(calc.scope3Total)}</b></Table.Cell>
              <Table.Cell>100%</Table.Cell>
              <Table.Cell><b>{fmt(calc.coveredEmissions)} tCO₂e seleccionadas</b></Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>

        {calc.scope3Required ? (
          calc.coveragePercent >= cobertura_a3 ? (
            <div className="sbti-note sbti-note--ok">
              <b>Cobertura suficiente:</b> las categorías seleccionadas suman {fmt(calc.coveragePercent)}% del A3
              elegible (mínimo {cobertura_a3}%). Los objetivos A3 del Paso 4 se calculan sobre esta base ({fmt(calc.coveredEmissions)} tCO₂e).
            </div>
          ) : (
            <div className="sbti-note sbti-note--warn">
              <b>Cobertura insuficiente:</b> seleccionaste {fmt(calc.coveragePercent)}% y el mínimo es {cobertura_a3}%
              del A3 elegible. Sumá categorías — en el sector, Cat 1 suele bastar por sí sola.
            </div>
          )
        ) : calc.scope3Total ? (
          <div className="sbti-note">
            <b>A3 &lt; {umbral_a3}%:</b> el objetivo de Alcance 3 no es obligatorio, aunque el screening y el reporte
            del inventario A3 siguen siéndolo.
          </div>
        ) : null}

        <div className="sbti-src">
          <b>Fuentes:</b> GHG Protocol Scope 3 Standard (15 categorías); Criterios V5.3.1 — screening obligatorio,
          umbral {umbral_a3}%, cobertura ≥ {cobertura_a3}% (NT C6); guía sectorial 2019 §4 y §6.
        </div>
      </div>

      <div className="sbti-card">
        <h3><span className="sbti-tag">FLAG</span> Screening de emisiones de bosque, tierra y agricultura</h3>
        <Form>
          <Form.Group widths="equal">
            <Form.Field>
              <label>Emisiones FLAG como % del total (A1+2+3)</label>
              <Input type="number" step="0.1" value={inv.flagPercent || ""} onChange={(_, d) => setInv({ flagPercent: d.value })} />
              <div className="sbti-hint">
                Fuentes FLAG típicas en indumentaria: <b>algodón</b>, <b>cuero</b>, caucho natural y celulósicas (MMCF).
              </div>
            </Form.Field>
            <Form.Field>
              <label>Notas de estimación FLAG</label>
              <TextArea rows={3} value={inv.flagNotes || ""} onChange={(_, d) => setInv({ flagNotes: d.value })} />
            </Form.Field>
          </Form.Group>
        </Form>
        {inv.flagPercent ? (
          calc.flagRequired ? (
            <div className="sbti-note sbti-note--warn">
              <b>FLAG ≥ {flag_umbral}%:</b> corresponde fijar un objetivo FLAG separado según la Guía FLAG del SBTi,
              además del compromiso público de no-deforestación.
            </div>
          ) : (
            <div className="sbti-note sbti-note--ok">
              <b>FLAG &lt; {flag_umbral}%:</b> no se requiere objetivo FLAG separado.
            </div>
          )
        ) : null}
      </div>
    </div>
  );
}
